import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from "@material-ui/core/MenuItem";
import InputMask from "react-input-mask";
import TextField from "@material-ui/core/TextField";
import { UseMutationResult } from "@tanstack/react-query";
import WatchLater from "@material-ui/icons/WatchLater";
import Switch from "@material-ui/core/Switch";
import Grid from "@material-ui/core/Grid";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../../../../../styles";
import { AttendanceLocalDepartmentSchedule } from "../../../../../../../../../../../@types/attendance-local-department-schedule";
import { AttendanceLocalDepartmentScheduleCreateData } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/schedule/create";
import { AttendanceLocalDepartmentScheduleUpdateData } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/schedule/update";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../@types/attendance-local-department";
import { timeformat } from "../../../../../../../../../../../utils/dateFormat";

const weekDays = [
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
    "Domingo",
];

type FormProps = {
    departmentId: AttendanceLocalDepartment["id"];
    existingSchedules?: AttendanceLocalDepartmentSchedule[];
    schedule?: AttendanceLocalDepartmentSchedule;
    buttonTrigger?: React.ReactNode;
    mutation: UseMutationResult<
        AttendanceLocalDepartmentSchedule,
        unknown,
        | AttendanceLocalDepartmentScheduleCreateData
        | AttendanceLocalDepartmentScheduleUpdateData,
        unknown
    >
};

export default function Form({ departmentId, existingSchedules, schedule, buttonTrigger, mutation }: FormProps) {
    const [open, setOpen] = useState(false);
    const { classes } = useFormStyles();
    const { classes: defaultClasses } = useAttendanceLocalStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            setor: departmentId.toString(),
            dia_semana: "",
            ativo: false,
            hora_inicio: "",
            hora_fim: "",
            motivo_inativo: null,
        }
    });

    const filteredWeekDays = useMemo(() => {
        if (!existingSchedules || existingSchedules.length === 0) {
            return weekDays;
        }

        const existingDays = existingSchedules.map(schedule => schedule.dia_semana);
        return weekDays.filter(day => !existingDays.includes(day));
    }, [existingSchedules]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            setor: Number(data.setor),
            dia_semana: data.dia_semana,
            ativo: data.ativo,
            hora_inicio: data.hora_inicio,
            hora_fim: data.hora_fim,
            intervalo_hora_inicio: data.intervalo_hora_inicio || null,
            intervalo_hora_fim: data.intervalo_hora_fim || null,
            motivo_inativo: data.motivo_inativo || null,
        });
    }

    useEffect(() => {
        if (open && schedule) {
            form.reset({
                setor: schedule.setor.toString(),
                dia_semana: schedule.dia_semana,
                ativo: schedule.ativo,
                hora_inicio: timeformat(schedule.hora_inicio) || "",
                hora_fim: timeformat(schedule.hora_fim) || "",
                intervalo_hora_inicio: timeformat(schedule.intervalo_hora_inicio) || undefined,
                intervalo_hora_fim: timeformat(schedule.intervalo_hora_fim) || undefined,
                motivo_inativo: schedule.motivo_inativo || null,
            });
        } else {
            form.reset();
        }
    }, [open, schedule, form]);

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Horário de Funcionamento
                </Button>
            )}
            <Dialog open={open}>
                <Card component="form" onSubmit={form.handleSubmit(handleSubmit)}>
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={<Typography variant="h5">Horário de Funcionamento</Typography>}
                    />
                    <CardContent
                        container
                        spacing={2}
                        component={Grid}
                        className={classes.cardContent}
                        style={{
                            paddingBottom: 0,
                        }}
                    >
                        <Grid item xs={12} component={Typography}>
                            Configuração de horários
                        </Grid>
                        {!schedule && (
                            <Grid item xs={12}>
                                <Controller
                                    name="dia_semana"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <FormControl
                                            variant="outlined"
                                            className={classes.formControl}
                                            error={!!fieldState.error}
                                        >
                                            <InputLabel id="week-days-label" required>
                                                Dia da semana
                                            </InputLabel>
                                            <Select
                                                id="week-days"
                                                readOnly={!!schedule}
                                                labelId="week-days-label"
                                                value={field.value}
                                                label="Dias da semana"
                                                onChange={(e) => {
                                                    field.onChange(e.target.value);
                                                }}
                                                MenuProps={{
                                                    anchorOrigin: {
                                                        vertical: "bottom",
                                                        horizontal: "left",
                                                    },
                                                    transformOrigin: {
                                                        vertical: "top",
                                                        horizontal: "left",
                                                    },
                                                    getContentAnchorEl: null,
                                                }}
                                            >
                                                {filteredWeekDays.map((weekDay) => (
                                                    <MenuItem key={weekDay} value={weekDay}>
                                                        {weekDay}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>
                                                {fieldState.error && fieldState.error.message}
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid item xs={6}>
                            <Controller
                                name="hora_inicio"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ? fieldState.error.message : ""
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="hora_fim"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                required
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do agendamento"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ? fieldState.error.message : ""
                                                }
                                                InputProps={{
                                                    required: false,
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} component={Typography}>
                            Configuração de intervalo
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_inicio"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Início do intervalo"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ? fieldState.error.message : ""
                                                }
                                                InputProps={{
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="intervalo_hora_fim"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <InputMask
                                        mask="99:99"
                                        maskChar={null}
                                        value={field.value || ""}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            field.onChange(e.target.value);
                                        }}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                margin="normal"
                                                variant="outlined"
                                                label="Fim do intervalo"
                                                inputProps={{ className: classes.textField }}
                                                error={!!fieldState.error}
                                                helperText={
                                                    fieldState.error ? fieldState.error.message : ""
                                                }
                                                InputProps={{
                                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} component={Typography}>
                            Configuração de atividade
                        </Grid>
                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="ativo"
                                control={form.control}
                                render={({ field }) => (
                                    <FormControl>
                                        <FormControlLabel
                                            label="Este horário/dia está ativo?"
                                            control={<Switch {...field} checked={field.value} />}
                                        />
                                        <FormHelperText>
                                            Ative esta opção para permitir que este horário/dia seja exibido como disponível.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {!form.watch("ativo") && (
                            <Grid item xs={12}>
                                <Controller
                                    name="motivo_inativo"
                                    control={form.control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            required
                                            fullWidth
                                            multiline
                                            margin="normal"
                                            variant="outlined"
                                            label="Motivo"
                                            inputProps={{ className: classes.textField }}
                                            error={!!fieldState.error}
                                            helperText={
                                                fieldState.error ? fieldState.error.message : ""
                                            }
                                            InputProps={{
                                                required: false,
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={defaultClasses.addButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? "Salvando..." : "Salvar"}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog>
        </>
    );
}
